import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ShoppingCart, Filter, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useCart } from '../contexts/CartContext';
import ProductModal from '../components/ProductModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Shop = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const { toast } = useToast();
  const { cart, addToCart: addToCartContext, getCartTotal } = useCart();

  // Fetch products from API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${API}/products`);
        if (!response.ok) throw new Error('Error loading products');
        const data = await response.json();
        setProducts(data);
      } catch (error) {
        console.error('Error:', error);
        toast({
          title: "Error",
          description: "No se pudieron cargar los productos",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [toast]);

  const categories = ['Todos', ...new Set(products.map(p => p.category))];

  const filteredProducts = selectedCategory === 'Todos' 
    ? products 
    : products.filter(p => p.category === selectedCategory);

  const handleAddToCart = (product) => {
    addToCartContext(product);
    toast({
      title: "Producto agregado",
      description: `${product.name} se agregó al carrito`,
    });
  };

  const cartTotal = getCartTotal();

  const handleCheckout = () => {
    if (cart.length === 0) {
      toast({
        title: "Carrito vacío",
        description: "Agrega productos antes de finalizar la compra",
        variant: "destructive"
      });
      return;
    }
    navigate('/cart');
  };

  if (loading) {
    return (
      <div className="min-h-screen py-20 px-4 flex items-center justify-center">
        <Loader2 className="h-12 w-12 text-cyan-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-20 px-4">
      <div className="container mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-12">
          <div>
            <h1 className="text-5xl md:text-6xl font-bold mb-4 bg-gradient-to-r from-cyan-400 to-emerald-400 bg-clip-text text-transparent">
              Tienda Online
            </h1>
            <p className="text-xl text-slate-300">Accesorios y componentes para tus dispositivos</p>
          </div>
          
          <Card className="bg-slate-800/50 backdrop-blur-sm border-slate-700">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <ShoppingCart className="h-6 w-6 text-cyan-400" />
                <div>
                  <div className="text-white font-semibold">Carrito</div>
                  <div className="text-slate-400">{cart.length} productos - ${cartTotal.toLocaleString()}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <Filter className="h-5 w-5 text-slate-400" />
            <span className="text-slate-300 font-semibold">Categorías:</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? "default" : "outline"}
                className={selectedCategory === category 
                  ? "bg-gradient-to-r from-cyan-600 to-emerald-600" 
                  : "border-slate-700 text-slate-300 hover:bg-slate-800"}
                onClick={() => setSelectedCategory(category)}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>

        {/* Products Grid */}
        {filteredProducts.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-slate-400 text-lg">No hay productos en esta categoría</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {filteredProducts.map((product) => (
              <Card key={product.id} className="bg-slate-800/50 backdrop-blur-sm border-slate-700 hover:border-cyan-500/50 transition-all duration-300 hover:scale-105 overflow-hidden relative">
                {product.discount > 0 && (
                  <div className="absolute top-4 right-4 bg-red-600 text-white px-3 py-1 rounded-full font-bold text-sm z-10 animate-pulse">
                    -{product.discount}%
                  </div>
                )}
                <div className="aspect-square overflow-hidden bg-slate-900">
                  <img 
                    src={product.image} 
                    alt={product.name} 
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <CardHeader>
                  <div className="flex justify-between items-start mb-2">
                    <Badge className="bg-cyan-500/20 text-cyan-400 border-cyan-500/30">
                      {product.category}
                    </Badge>
                    {!product.stock && (
                      <Badge variant="destructive">Sin Stock</Badge>
                    )}
                  </div>
                  <CardTitle className="text-white">{product.name}</CardTitle>
                  <CardDescription className="text-slate-400">{product.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex justify-between items-center">
                    <div>
                      {product.discount > 0 ? (
                        <div className="flex flex-col">
                          <span className="text-slate-400 line-through text-sm">
                            {product.currency === 'USD' ? `$${product.price} USD` : `$${product.price.toLocaleString()} ARS`}
                          </span>
                          <span className="text-2xl font-bold text-emerald-400">
                            {product.currency === 'USD' 
                              ? `$${Math.round(product.price * (1 - product.discount / 100))} USD`
                              : `$${Math.round(product.price * (1 - product.discount / 100)).toLocaleString()} ARS`
                            }
                          </span>
                          <span className="text-red-400 text-xs font-bold">
                            {product.discount}% OFF
                          </span>
                        </div>
                      ) : (
                        <span className="text-2xl font-bold text-cyan-400">
                          {product.currency === 'USD' ? `$${product.price} USD` : `$${product.price.toLocaleString()} ARS`}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm"
                        variant="outline"
                        className="flex-1 border-cyan-600 text-cyan-400 hover:bg-cyan-600/10"
                        onClick={() => navigate(`/product/${product.id}`)}
                      >
                        Ver detalles
                      </Button>
                      <Button 
                        size="sm"
                        className="flex-1 bg-gradient-to-r from-cyan-600 to-emerald-600 hover:from-cyan-500 hover:to-emerald-500"
                        onClick={() => handleAddToCart(product)}
                        disabled={!product.stock}
                      >
                        <ShoppingCart className="h-4 w-4 mr-1" />
                        Agregar
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Floating Cart Button */}
        {cart.length > 0 && (
          <div className="fixed bottom-6 right-6 z-50">
            <Card className="bg-slate-800 border-slate-700 shadow-2xl">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div>
                    <div className="text-white font-semibold">{cart.length} productos</div>
                    <div className="text-2xl font-bold text-cyan-400">${cartTotal.toLocaleString()}</div>
                  </div>
                  <Button 
                    className="bg-gradient-to-r from-cyan-600 to-emerald-600 hover:from-cyan-500 hover:to-emerald-500"
                    onClick={handleCheckout}
                  >
                    Ver Carrito y Finalizar
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default Shop;
